import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AUTH, registerSchema } from '@en3/shared';
import { toDisplayMessage } from '@/api/errors';
import { useRegister } from '@/api/hooks/useAuth';
import { ROUTES } from '@/app/routes';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { serverFieldErrors, zodFieldErrors } from '@/lib/form';

/**
 * 注册页。校验与后端共用 `registerSchema`（含密码长度下限，来自 `AUTH.passwordMinLength`）。
 */
export function RegisterPage() {
  const navigate = useNavigate();
  const register = useRegister();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFormError(null);

    const parsed = registerSchema.safeParse({
      email,
      password,
      ...(displayName.trim() ? { displayName: displayName.trim() } : {}),
    });
    if (!parsed.success) {
      setErrors(zodFieldErrors(parsed.error));
      return;
    }
    setErrors({});

    try {
      await register.mutateAsync(parsed.data);
      navigate(ROUTES.home, { replace: true });
    } catch (error) {
      setErrors(serverFieldErrors(error));
      setFormError(toDisplayMessage(error));
    }
  }

  return (
    <section className="mx-auto max-w-md px-5 py-10">
      <h1 className="text-2xl font-bold">注册</h1>
      <p className="mt-1 text-sm text-muted-foreground">创建账号后即可保存生词本与复习进度。</p>

      <form onSubmit={handleSubmit} noValidate className="mt-6 space-y-4">
        <Field id="email" label="邮箱" error={errors.email} hint="用于登录，不会公开展示">
          <Input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            inputMode="email"
            placeholder="you@example.com"
            value={email}
            aria-invalid={errors.email ? true : undefined}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>

        <Field
          id="password"
          label="密码"
          error={errors.password}
          hint={`至少 ${AUTH.passwordMinLength} 位，建议包含字母与数字`}
        >
          <Input
            id="password"
            name="password"
            type="password"
            autoComplete="new-password"
            placeholder="设置一个密码"
            value={password}
            aria-invalid={errors.password ? true : undefined}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>

        <Field id="displayName" label="昵称（可选）" error={errors.displayName} hint="不填则用邮箱前缀">
          <Input
            id="displayName"
            name="displayName"
            type="text"
            autoComplete="nickname"
            placeholder="例如：小明"
            value={displayName}
            aria-invalid={errors.displayName ? true : undefined}
            onChange={(e) => setDisplayName(e.target.value)}
          />
        </Field>

        {formError && (
          <p role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {formError}
          </p>
        )}

        <Button type="submit" className="w-full" loading={register.isPending}>
          注册并登录
        </Button>
      </form>

      <p className="mt-6 text-sm text-muted-foreground">
        已经有账号？
        <Link className="ml-1 text-primary underline underline-offset-4" to={ROUTES.login}>
          去登录
        </Link>
      </p>
      <p className="mt-2 text-xs text-muted-foreground">
        注册即表示你同意平台仅保存学习所必需的数据；可在个人中心申请删除账号与全部学习数据。
      </p>
    </section>
  );
}
